import React, {ReactNode} from 'react'
import styled from 'styled-components'
import {Box} from '@mui/material'
import {ApplyTitle, FirstPageDiv, HeaderArea, HeaderAreaBox} from './NatAdminApplicatioPrintCommonStyle'
import {getPrintDate} from '../../helper/NatApplicationHelper'
import {StyledDiv, StyledImg} from '../../../../../../styledComponents/StyledTypeDefinitions'
import {NatSealField, SealField} from './NatSealField'
import {NatEncloseInParenthesis} from './NatEncloseInParenthesis'
import moment from 'moment'
import {
    APPLY_IRREGULAR_PATTERN,
    PET_TYPE,
    RESIDENT_APPLY_PROCESS,
    RESIDENT_APPLY_STATUS
} from '../../../../../../CodeMaster'
import {ApplyStatusInfo} from '../../../repository/NatGetApplicationStatus'
import {convertToRoomNumberForResident} from '../../../../../common/util/Natutil'
import {NatPetBreedingDetail} from '../../../repository/NatGetPetBreeding'

interface Props {
    petBreedingApplicationDocumentParam: NatPetBreedingDetail
    applyStatusInfoList: ApplyStatusInfo[]
    healthCenterDogLicenseDataUrl: string
    rabiesPreventionInjectionCertificationDataUrl: string
}

export const NatPetBreedingApplicationDocument: React.FC<Props> = props => {
    const {
        petBreedingApplicationDocumentParam,
        applyStatusInfoList,
        healthCenterDogLicenseDataUrl,
        rabiesPreventionInjectionCertificationDataUrl
    } = props

    const {
        applyId, condominiumName, ridgeName, roomNumber,
        applicantFamilyName, applicantFirstName, petType, petBreed,
        havePetStartMonth
    } = petBreedingApplicationDocumentParam

    const applicantRoom = `${ridgeName} ${convertToRoomNumberForResident(roomNumber)}`
    const applicantName = `${applicantFamilyName} ${applicantFirstName}`
    const petTypeLabel = `${PET_TYPE.codeToEnumConstants(petType).label}`

    return (<>
        <FirstPageDiv>
            <HeaderArea>
                <HeaderAreaBox>
                    <Box ml={'auto'} mt={'10mm'}>申請ID：{applyId}</Box>
                </HeaderAreaBox>
                <HeaderAreaBox>
                    <Box m={'auto'}><ApplyTitle>ペット飼育申請書</ApplyTitle></Box>
                </HeaderAreaBox>
                <HeaderAreaBox>
                    <Box ml={'auto'} mt={'3mm'}>
                        {renderSealField(applyStatusInfoList)}
                    </Box>
                </HeaderAreaBox>
            </HeaderArea>
            <Box ml={'25mm'} mt={'3mm'}>
                理事長殿
            </Box>
            <Box ml={'30mm'} mt={'5mm'} whiteSpace={'pre-wrap'}>
                　私は、ペットの飼育にあたっては法的事項及び飼育規則を遵守し<br/>
                他に危害・迷惑をかけないことを誓います。<br/>
                万一違反した場合は、飼育を禁止されても異議を申し立てません。
            </Box>
            <Box margin={'3mm 0 0 30mm'} display={'flex'} flexDirection={'column'} width={'130mm'}>
                <Box>
                    <Box mb={'5mm'} whiteSpace={'pre-wrap'}>1  申請者</Box>
                    <Box ml={'8mm'} mb={'5mm'}>
                        {renderApplicationDetail('マンション名', condominiumName)}
                        {renderApplicationDetail('部屋番号', applicantRoom)}
                        {renderApplicationDetail('氏名', applicantName)}
                    </Box>
                </Box>
                <Box mb={'5mm'}>
                    <Box mb={'5mm'} whiteSpace={'pre-wrap'}>2  飼育するペット</Box>
                    <Box ml={'8mm'}>
                        {renderApplicationDetail('種類', petTypeLabel)}
                        {renderApplicationDetail('犬種等', petBreed)}
                        {renderApplicationDetail('飼育開始月', renderUseStartDate(havePetStartMonth))}
                    </Box>
                </Box>
                {(petType === PET_TYPE.DOG.value) ?
                    <Box mb={'5mm'}>
                        <Box mb={'5mm'} whiteSpace={'pre-wrap'}>3  添付書類</Box>
                    </Box> : <></>
                }
            </Box>
            {(petType === PET_TYPE.DOG.value) ? <>
                <AttachedDocumentleft>
                    <Box ml={'25mm'}>保健所犬鑑札</Box>
                    <Box ml={'25mm'}>
                        <StyledCardContent>
                            <CondominiumImg src={
                                healthCenterDogLicenseDataUrl
                            }/>
                        </StyledCardContent>
                    </Box>
                </AttachedDocumentleft>
                <AttachedDocumentright>
                    <Box mr={'27mm'}>狂犬病予防注射済証</Box>
                    <Box mr={'27mm'}>
                        <StyledCardContent>
                            <CondominiumImg src={
                                rabiesPreventionInjectionCertificationDataUrl
                            }/>
                        </StyledCardContent>
                    </Box>
                </AttachedDocumentright>
            </> : <></>
            }
        </FirstPageDiv>
    </>)

    NatPetBreedingApplicationDocument.displayName = 'NatPetBreedingApplicationDocument'
}

const renderApplicationDetail = (title: string, value: string | ReactNode) => {
    return (
        <PrintApplicationDetailContainer>
            <Box whiteSpace={'pre-wrap'}>{title}</Box>
            <NatEncloseInParenthesis item={value}/>
        </PrintApplicationDetailContainer>
    )
}

const renderUseStartDate = (date: string) => {
    const value = getPrintDate(date)

    return (
        <>
            <Box component={'span'} padding={'0 3mm 0 1mm'}>{value.year}</Box>年
            <Box component={'span'} padding={'0 4mm 0 4mm'}>{value.month}</Box>月
        </>
    )
}

const renderSealField = (applyStatusInfoList: ApplyStatusInfo[]) => {
    const sealFields: SealField[] = applyStatusInfoList.map((info) => {
        const applyStatusEnum = RESIDENT_APPLY_PROCESS.codeToEnumConstants(info.applyProcess)
        let seal
        if (info.applyStatus === RESIDENT_APPLY_STATUS.REJECTION.value) {
            seal = '却下'
        } else if (info.actual.irregularPattern === APPLY_IRREGULAR_PATTERN.NO_PET_CLUB.value) {
            seal = '-'
        } else {
            seal = info.actual.registerDatetime ? moment(info.actual.registerDatetime).format('YYYY/M/D') : ''
        }
        return {
            applyStatus: applyStatusEnum.label,
            seal: seal,
            sortOrder: applyStatusEnum.displayOrder
        }
    });

    return (
        <NatSealField sealFields={sealFields}/>
    )
}

const PrintApplicationDetailContainer: StyledDiv = styled.div`
  display: flex;
  width: 100%;
`
const CondominiumImg: StyledImg = styled.img`
    max-width: 60mm;
    max-height: 44mm;
`;

const StyledCardContent = styled.div`
  border: solid 0.5pt black;
  width: 65mm;
  height: 70mm;
  display: flex;
  align-items: center;
  justify-content: center;
`
const AttachedDocumentleft: StyledDiv = styled.div`
   float:left;
`
const AttachedDocumentright: StyledDiv = styled.div`
   float:right;
`
